import React, { useState, useEffect, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import darkModeIcon from "./darkBT.gif";
import logoQWER from "./log_QWER.jpeg";
import mini3rd from "./3rdmini.jpg";
import endGif1 from "./end1.gif";
import endGif2 from "./end2.gif";
import endGif3 from "./end3.gif";
import { useTranslation } from "react-i18next";
import i18n from "../src/lang/i18n";
import "./TestTimeline.css";

const extractYouTubeID = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const formatDate = (dateObject) => {
  if (dateObject && dateObject.$date) {
    return dateObject.$date.split("T")[0];
  }
  return "";
};

const isMobile = () => {
  return /Mobi|Android|iPhone/i.test(navigator.userAgent);
};

const getRandomEndGif = () => {
  const gifs = [endGif1, endGif2, endGif3];
  return gifs[Math.floor(Math.random() * gifs.length)];
};

const FanCam = ({ darkMode, toggleDarkMode, language, langbt }) => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loader = useRef(null);
  const scrollTimeout = useRef(null);
  const [endGif, setEndGif] = useState(getRandomEndGif());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [eventList, setEventList] = useState([]);
  const [isEventListOpen, setIsEventListOpen] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const imageObserver = useRef(null);

  const itemsPerPage = 5;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await fetch("/fancamData.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.uploadDate.$date);
          const dateB = new Date(b.uploadDate.$date);
          return dateA - dateB;
        });
        setAllItems(sortedData);
        setItems(sortedData.slice(0, itemsPerPage));
        setHasMore(sortedData.length > itemsPerPage);
      } catch (error) {
        console.error("Error loading JSON data:", error);
      }
      setIsLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchEventList = async () => {
      try {
        const response = await fetch("/eventList.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setEventList(data);
      } catch (error) {
        console.error("Error loading event list:", error);
      }
    };
    fetchEventList();
  }, []);

  useEffect(() => {
    imageObserver.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.getAttribute("data-src");
            if (src) {
              img.src = src;
              img.removeAttribute("data-src");
              setLoadedImages((prev) => new Set([...prev, src]));
            }
            imageObserver.current.unobserve(img);
          }
        });
      },
      {
        rootMargin: "50px 0px",
        threshold: 0.1,
      }
    );

    return () => {
      if (imageObserver.current) {
        imageObserver.current.disconnect();
      }
    };
  }, []);

  const imageRef = useCallback((node) => {
    if (node && imageObserver.current) {
      imageObserver.current.observe(node);
    }
  }, []);

  const loadMoreItems = async () => {
    if (isLoading || !hasMore) return;

    const filteredItems = allItems.filter((item) => {
      const itemDate = new Date(item.uploadDate.$date);
      return (
        (!selectedEvent || (item.tag && item.tag.includes(selectedEvent))) &&
        (!selectedDate ||
          (itemDate.getFullYear() === selectedDate.getFullYear() &&
            itemDate.getMonth() === selectedDate.getMonth()))
      );
    });

    const nextPage = page + 1;
    const startIndex = nextPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const newItems = filteredItems.slice(startIndex, endIndex);

    if (newItems.length === 0) {
      setHasMore(false);
      return;
    }

    setIsLoading(true);
    try {
      const delay = isMobile() ? 150 : 300;
      await new Promise((resolve) => setTimeout(resolve, delay));

      setItems((prevItems) => [...prevItems, ...newItems]);
      setPage(nextPage);
      setHasMore(endIndex < filteredItems.length);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = () => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    const debounceTime = isMobile() ? 100 : 150;

    scrollTimeout.current = setTimeout(() => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      let scrollThreshold;
      if (isMobile()) {
        scrollThreshold = documentHeight * 0.5; // 모바일은 50% 지점
      } else {
        scrollThreshold = documentHeight - windowHeight - 100; // 데스크탑은 기존 방식
      }

      if (scrollTop >= scrollThreshold) {
        loadMoreItems();
      }
    }, debounceTime);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [isLoading, items, hasMore]);

  const getYouTubeAppLink = (url) => {
    const videoId = extractYouTubeID(url);
    if (!videoId) return url;
    if (/Android/i.test(navigator.userAgent)) {
      return `intent://www.youtube.com/watch?v=${videoId}#Intent;package=com.google.android.youtube;scheme=https;end`;
    }
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const handleThumbnailClick = (url) => {
    const link = isMobile() ? getYouTubeAppLink(url) : url;
    window.open(link, "_blank");
  };

  const handleEventFilter = (event) => {
    const newEvent = event === selectedEvent ? null : event;
    setSelectedEvent(newEvent);
    setIsEventListOpen(false);

    const filteredItems = allItems.filter((item) => {
      const itemDate = new Date(item.uploadDate.$date);
      return (
        (!newEvent || (item.tag && item.tag.includes(newEvent))) &&
        (!selectedDate ||
          (itemDate.getFullYear() === selectedDate.getFullYear() &&
            itemDate.getMonth() === selectedDate.getMonth()))
      );
    });

    setItems(filteredItems.slice(0, itemsPerPage));
    setPage(0);
    setHasMore(filteredItems.length > itemsPerPage);
  };

  const handleDateFilter = () => {
    const filteredItems = allItems.filter((item) => {
      const itemDate = new Date(item.uploadDate.$date);
      return (
        !selectedDate ||
        (itemDate.getFullYear() === selectedDate.getFullYear() &&
          itemDate.getMonth() === selectedDate.getMonth())
      );
    });
    setItems(filteredItems.slice(0, itemsPerPage));
    setPage(0);
  };

  const toggleEventList = () => {
    setIsEventListOpen(!isEventListOpen);
  };

  const clearEventFilter = (e) => {
    e.stopPropagation();
    setSelectedEvent("");
    const filteredItems = allItems.filter((item) => {
      const itemDate = new Date(item.uploadDate.$date);
      return (
        !selectedDate ||
        (itemDate.getFullYear() === selectedDate.getFullYear() &&
          itemDate.getMonth() === selectedDate.getMonth())
      );
    });
    setItems(filteredItems.slice(0, itemsPerPage));
    setPage(0);
  };

  return (
    <div className={`FanCam ${darkMode ? "dark-mode" : ""}`}>
      <h1>{language("FanCam")}</h1>
      <h3>{language("dev_memo_1")}</h3>
      {/* <button className="toggle-button" onClick={toggleDarkMode}>
        <div className="bwicon"></div>
        <span>{language("switMode")}</span>
      </button> */}
      <div class="lang-switch">
        <button
          className={`lang-btn ${i18n.language === "ko" ? "active" : ""}`}
          onClick={langbt.ko}
        >
          KR
        </button>
        <button
          className={`lang-btn ${i18n.language === "en" ? "active" : ""}`}
          onClick={langbt.en}
        >
          EN
        </button>
        <button
          className={`lang-btn ${i18n.language === "jp" ? "active" : ""}`}
          onClick={langbt.jp}
        >
          JP
        </button>
      </div>

      <div
        className="date-input-container"
        style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}
      >
        <DatePicker
          selected={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            // 날짜가 선택되면 자동으로 필터링
            const filteredItems = allItems.filter((item) => {
              const itemDate = new Date(item.uploadDate.$date);
              return (
                !date ||
                (itemDate.getFullYear() === date.getFullYear() &&
                  itemDate.getMonth() === date.getMonth())
              );
            });
            setItems(filteredItems.slice(0, itemsPerPage));
            setPage(0);
          }}
          dateFormat="yyyy/MM"
          showMonthYearPicker
          placeholderText="YYYY/MM"
          style={{
            fontSize: "18px",
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            width: "150px",
            textAlign: "center",
          }}
        />
      </div>

      <div className="event-picker">
        <div className="event-list-header" onClick={toggleEventList}>
          <h3 className="event-list-title">
            {selectedEvent ? language(selectedEvent) : language("filterTitle")}
          </h3>
          <div className="header-controls">
            {selectedEvent && (
              <button
                className="clear-filter"
                onClick={clearEventFilter}
                aria-label="Clear filter"
              >
                ×
              </button>
            )}
            <span
              className={`event-list-toggle ${isEventListOpen ? "open" : ""}`}
            >
              ▼
            </span>
          </div>
        </div>
        <div className={`event-list-content ${isEventListOpen ? "open" : ""}`}>
          {eventList.map((event, index) => (
            <div
              key={index}
              className={`event-item ${
                selectedEvent === event ? "selected" : ""
              }`}
              onClick={() => handleEventFilter(event)}
            >
              {language(event)}
            </div>
          ))}
        </div>
      </div>
      <div className="test-timeline">
        {items.map((item, index) => {
          const videoId = extractYouTubeID(item.url);
          const thumbnailUrl = videoId
            ? `https://img.youtube.com/vi/${videoId}/0.jpg`
            : "";
          return (
            <div
              className={`test-timeline-item ${
                index % 2 === 0 ? "left" : "right"
              }`}
              key={item._id}
            >
              <div className="test-timeline-circle">
                <img
                  src={mini3rd}
                  alt="QWER Logo"
                  className="test-timeline-circle-img"
                />
              </div>
              <div className="test-timeline-card">
                {thumbnailUrl && (
                  <img
                    src={thumbnailUrl}
                    alt={item.title}
                    className="thumbnail"
                    onClick={() => handleThumbnailClick(item.url)}
                  />
                )}
                <div className="test-timeline-title-container">
                  <h2>{item.title}</h2>
                </div>
                <div className="test-timeline-date">
                  {formatDate(item.uploadDate)}
                </div>
                <a
                  href={isMobile() ? getYouTubeAppLink(item.churl) : item.churl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="channel-label"
                >
                  {language("ch")}: {item.source}
                </a>
                <a
                  href={isMobile() ? getYouTubeAppLink(item.url) : item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="test-timeline-link"
                >
                  {language("watch")}
                </a>
              </div>
            </div>
          );
        })}
        <div ref={loader} className="loader">
          {isLoading && (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>{language("loading")}</p>
            </div>
          )}
        </div>
      </div>
      <footer className="footer">{language("footer")}</footer>
    </div>
  );
};

export default FanCam;
